import { ConfirmDeleteDialog } from '@/components/features/shared/ConfirmDeleteDialog'
import { LoaderElement } from '@/components/features/shared/LoaderElement'
import { NavHeader } from '@/components/features/shared/NavHeader'
import { PageContainer } from '@/components/features/shared/PageContainer'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { User, UserRole } from '@/domain/auth/auth'
import { Collector } from '@/domain/collectors/collector'
import { useCollectors, useCreateCollector, useDeleteCollector, useUpdateCollector } from '@/hooks/api/useCollectors'
import { useCreateSeller, useDeleteSeller, useSellers, useUpdateSeller } from '@/hooks/api/useSellers'
import { useCreateUser, useDeleteUser, useUpdateUser, useUsers } from '@/hooks/api/useUsers'
import { useFrontendPagination } from '@/hooks/useFrontendPagination'
import { PaginationState } from '@tanstack/react-table'
import { useCallback, useState } from 'react'
import { CollectorDialog } from './CollectorDialog'
import { CollectorTable } from './CollectorTable'
import { SellerTable } from './SellerTable'
import { UserDialog } from './UserDialog'
import { UserTable } from './UserTable'

type TabType = 'users' | 'collectors' | 'sellers'

export function UsersPage() {
  const [activeTab, setActiveTab] = useState<TabType>('users')
  const [userDialogOpen, setUserDialogOpen] = useState(false)
  const [collectorDialogOpen, setCollectorDialogOpen] = useState(false)
  const [sellerDialogOpen, setSellerDialogOpen] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [editingCollector, setEditingCollector] = useState<Collector | null>(null)
  const [editingSeller, setEditingSeller] = useState<Collector | null>(null)
  const [userPagination, setUserPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 10 })
  const [confirmDeleteUser, setConfirmDeleteUser] = useState<string | null>(null)
  const [confirmDeleteCollector, setConfirmDeleteCollector] = useState<string | null>(null)
  const [confirmDeleteSeller, setConfirmDeleteSeller] = useState<string | null>(null)

  const { data: usersResult } = useUsers({
    roles: [UserRole.Employee, UserRole.Secretary, UserRole.Admin, UserRole.SuperAdmin],
    page: userPagination.pageIndex + 1,
    limit: userPagination.pageSize,
  })

  const users = usersResult?.data ?? []
  const totalUsers = usersResult?.total ?? 0

  const { data: collectorsData, isLoading: isLoadingCollectors } = useCollectors()
  const collectors = collectorsData ?? []
  const { paginatedData: paginatedCollectors, pagination: frontendPagination, setPagination: setFrontendPagination, totalRecords: totalCollectors } = useFrontendPagination(collectors, { initialPageSize: 100 })

  const { data: sellersData, isLoading: isLoadingSellers } = useSellers()
  const sellers = sellersData ?? []
  const { paginatedData: paginatedSellers, pagination: frontendSellerPagination, setPagination: setFrontendSellerPagination, totalRecords: totalSellers } = useFrontendPagination(sellers, { initialPageSize: 100 })

  const createUserMutation = useCreateUser()
  const updateUserMutation = useUpdateUser()
  const deleteUserMutation = useDeleteUser()

  const createCollectorMutation = useCreateCollector()
  const updateCollectorMutation = useUpdateCollector()
  const deleteCollectorMutation = useDeleteCollector()

  const createSellerMutation = useCreateSeller()
  const updateSellerMutation = useUpdateSeller()
  const deleteSellerMutation = useDeleteSeller()

  const handleCreateUser = useCallback(() => {
    setEditingUser(null)
    setUserDialogOpen(true)
  }, [])

  const handleEditUser = useCallback((user: User) => {
    setEditingUser(user)
    setUserDialogOpen(true)
  }, [])

  const handleDeleteUser = useCallback((userId: string) => {
    setConfirmDeleteUser(userId)
  }, [])

  const handleUserSubmit = useCallback(
    (data: any) => {
      if (editingUser) {
        updateUserMutation.mutateAsync({ id: editingUser.id, data })
      } else {
        createUserMutation.mutateAsync(data)
      }
      setUserDialogOpen(false)
    },
    [editingUser, updateUserMutation, createUserMutation]
  )

  const handleUserDeleteConfirm = useCallback(() => {
    if (confirmDeleteUser) {
      deleteUserMutation.mutateAsync(confirmDeleteUser)
      setConfirmDeleteUser(null)
    }
  }, [confirmDeleteUser, deleteUserMutation])

  const handleCreateCollector = useCallback(() => {
    setEditingCollector(null)
    setCollectorDialogOpen(true)
  }, [])

  const handleEditCollector = useCallback((collector: Collector) => {
    setEditingCollector(collector)
    setCollectorDialogOpen(true)
  }, [])

  const handleDeleteCollector = useCallback((collectorId: string) => {
    setConfirmDeleteCollector(collectorId)
  }, [])

  const handleCollectorSubmit = useCallback(
    (data: any) => {
      if (editingCollector) {
        updateCollectorMutation.mutateAsync({ id: editingCollector.id, data })
      } else {
        createCollectorMutation.mutateAsync(data)
      }
      setCollectorDialogOpen(false)
    },
    [editingCollector, updateCollectorMutation, createCollectorMutation]
  )

  const handleCollectorDeleteConfirm = useCallback(() => {
    if (confirmDeleteCollector) {
      deleteCollectorMutation.mutateAsync(confirmDeleteCollector)
      setConfirmDeleteCollector(null)
    }
  }, [confirmDeleteCollector, deleteCollectorMutation])

  const handleCreateSeller = useCallback(() => {
    setEditingSeller(null)
    setSellerDialogOpen(true)
  }, [])

  const handleEditSeller = useCallback((seller: Collector) => {
    setEditingSeller(seller)
    setSellerDialogOpen(true)
  }, [])

  const handleDeleteSeller = useCallback((sellerId: string) => {
    setConfirmDeleteSeller(sellerId)
  }, [])

  const handleSellerSubmit = useCallback(
    (data: any) => {
      if (editingSeller) {
        updateSellerMutation.mutateAsync({ id: editingSeller.id, data })
      } else {
        createSellerMutation.mutateAsync(data)
      }
      setSellerDialogOpen(false)
    },
    [editingSeller, updateSellerMutation, createSellerMutation]
  )

  const handleSellerDeleteConfirm = useCallback(() => {
    if (confirmDeleteSeller) {
      deleteSellerMutation.mutateAsync(confirmDeleteSeller)
      setConfirmDeleteSeller(null)
    }
  }, [confirmDeleteSeller, deleteSellerMutation])

  if (isLoadingSellers || isLoadingCollectors) return <LoaderElement />

  return (
    <PageContainer>
      <NavHeader hideBackButton>
        <h1 className="text-3xl font-bold tracking-tight">Gestion de Usuarios</h1>
        <p className="text-muted-foreground">
          Administracion de usuarios, cobradores y vendedores del sistema
        </p>
      </NavHeader>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabType)} className="space-y-4">
        <TabsList>
          <TabsTrigger value="users">
            Usuarios ({users?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="collectors">
            Cobradores ({collectors?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="sellers">
            Vendedores ({totalSellers || sellers.length || 0})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="space-y-4">
          <UserTable
            users={users}
            totalUsers={totalUsers}
            pagination={userPagination}
            onPaginationChange={setUserPagination}
            onCreateUser={handleCreateUser}
            onEditUser={handleEditUser}
            onDeleteUser={handleDeleteUser}
            searchKey="email"
            searchPlaceholder="Buscar por nombre, email..."
          />
        </TabsContent>

        <TabsContent value="collectors" className="space-y-4">
          <CollectorTable
            collectors={paginatedCollectors}
            totalCollectors={totalCollectors}
            pagination={frontendPagination}
            onPaginationChange={setFrontendPagination}
            onCreateCollector={handleCreateCollector}
            onEditCollector={handleEditCollector}
            onDeleteCollector={handleDeleteCollector}
            searchKey="name"
            searchPlaceholder="Buscar cobrador..."
          />
        </TabsContent>

        <TabsContent value="sellers" className="space-y-4">
          <SellerTable
            sellers={paginatedSellers}
            totalSellers={totalSellers}
            pagination={frontendSellerPagination}
            onPaginationChange={setFrontendSellerPagination}
            onCreateSeller={handleCreateSeller}
            onEditSeller={handleEditSeller}
            onDeleteSeller={handleDeleteSeller}
            searchKey="name"
            searchPlaceholder="Buscar vendedor..."
          />
        </TabsContent>
      </Tabs>

      <UserDialog
        open={userDialogOpen}
        onOpenChange={setUserDialogOpen}
        user={editingUser}
        onSubmit={handleUserSubmit}
        isPending={createUserMutation.isPending || updateUserMutation.isPending}
      />

      <CollectorDialog
        open={collectorDialogOpen}
        onOpenChange={setCollectorDialogOpen}
        collector={editingCollector}
        onSubmit={handleCollectorSubmit}
        isPending={createCollectorMutation.isPending || updateCollectorMutation.isPending}
      />

      <CollectorDialog
        open={sellerDialogOpen}
        onOpenChange={setSellerDialogOpen}
        collector={editingSeller}
        onSubmit={handleSellerSubmit}
        isPending={createSellerMutation.isPending || updateSellerMutation.isPending}
      />

      <ConfirmDeleteDialog
        open={!!confirmDeleteUser}
        title="Confirmar Eliminacion"
        message="Estas seguro de que deseas eliminar este usuario? Esta accion no se puede deshacer."
        onOpenChange={(open) => !open && setConfirmDeleteUser(null)}
        onConfirm={handleUserDeleteConfirm}
      />

      <ConfirmDeleteDialog
        open={!!confirmDeleteCollector}
        title="Confirmar Eliminacion"
        message="Estas seguro de que deseas eliminar este cobrador? Esta accion no se puede deshacer."
        onOpenChange={(open) => !open && setConfirmDeleteCollector(null)}
        onConfirm={handleCollectorDeleteConfirm}
      />

      <ConfirmDeleteDialog
        open={!!confirmDeleteSeller}
        title="Confirmar Eliminacion"
        message="Estas seguro de que deseas eliminar este vendedor? Esta accion no se puede deshacer."
        onOpenChange={(open) => !open && setConfirmDeleteSeller(null)}
        onConfirm={handleSellerDeleteConfirm}
      />
    </PageContainer>
  )
}
